import React from 'react';
import { FileCheck2, Sparkles, Brain, AlertTriangle, Shuffle } from 'lucide-react';
import VerificationBadge, { relativeTime } from './VerificationBadge';

/** Runtime source chip with explanation tooltip. */
export default function RuntimeSourcePanel({ caseObj, onOpenTemplate }) {
  if (!caseObj) return null;
  const sel = caseObj.template_selection || {};
  const source = caseObj.runtime_source || 'unresolved';
  const fallback = caseObj.fallback_used;
  const ctx = caseObj.conversation_context || {};
  const unanswered = ctx.unanswered_required || [];

  const sourceLabel = {
    'template-driven': 'Template-driven',
    'hybrid': 'Hybrid (template + adaptive)',
    'adaptive-only': 'Adaptive AI',
    'unresolved': 'Pre-intake',
  }[source] || source;

  const SourceIcon = {
    'template-driven': FileCheck2,
    'hybrid': Shuffle,
    'adaptive-only': Brain,
    'unresolved': Sparkles,
  }[source] || Sparkles;

  return (
    <section className="border border-gray-200 bg-white" data-testid="runtime-source-panel">
      <header className="px-5 py-3 border-b border-gray-200 bg-surface flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="font-mono text-[11px] uppercase tracking-widest text-gray-700">Runtime Source</h3>
      </header>
      <div className="p-5 space-y-4 text-sm">
        {/* Source chip */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 text-primary-700 border border-primary-200 rounded-sm text-xs font-mono uppercase tracking-widest">
            <SourceIcon className="w-3.5 h-3.5" />
            {sourceLabel}
          </span>
          {sel.verification_status_used && (
            <VerificationBadge status={sel.verification_status_used} lastTestedAt={sel.last_tested_at} />
          )}
          {sel.published_with_override && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
              <AlertTriangle className="w-3 h-3" /> Override publish · risk
            </span>
          )}
          {fallback && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
              Fallback used
            </span>
          )}
        </div>

        {/* Template summary */}
        {sel.template_name ? (
          <div className="space-y-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Template powering flow</span>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="open-template-btn"
                onClick={() => onOpenTemplate?.(sel.selected_template_id)}
                className="text-base font-semibold text-primary hover:underline"
              >
                {sel.template_name}
              </button>
              <span className="text-xs font-mono text-gray-500">
                · {sel.family || '—'} {sel.variant ? `· ${sel.variant}` : ''} {sel.lane ? `· ${sel.lane}` : ''}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No template bound to this case — adaptive AI is driving intake.</p>
        )}

        {/* Reason */}
        {sel.reason && (
          <div className="border-l-2 border-primary pl-3 py-1 text-xs text-gray-700 bg-surface">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-0.5">Selection reason</span>
            {sel.reason}
          </div>
        )}

        {/* Fallback reason */}
        {fallback && caseObj.fallback_reason && (
          <div className="border-l-2 border-yellow-500 pl-3 py-1 text-xs text-yellow-800 bg-yellow-50">
            <span className="font-mono text-[10px] uppercase tracking-widest text-yellow-600 block mb-0.5">Fallback reason</span>
            {caseObj.fallback_reason}
          </div>
        )}

        {/* Unanswered required */}
        {unanswered.length > 0 && (
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">
              Unanswered required fields ({unanswered.length})
            </span>
            <div className="flex flex-wrap gap-1">
              {unanswered.map(q => (
                <span key={q} className="px-1.5 py-0.5 text-[11px] font-mono bg-orange-50 text-orange-700 border border-orange-200 rounded-sm">
                  {q}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Top candidates */}
        {(sel.top_candidates || []).length > 0 && (
          <details className="border-t border-gray-100 pt-3">
            <summary className="text-[11px] font-mono uppercase tracking-widest text-gray-500 cursor-pointer">
              Ranking detail · {sel.candidates_considered} candidates considered
            </summary>
            <ol className="mt-2 space-y-1.5 text-xs">
              {sel.top_candidates.map((c, i) => (
                <li key={c.id} className={`px-2 py-1.5 rounded flex items-center justify-between gap-2 ${
                  i === 0 ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50 border border-gray-100'
                }`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[10px] text-gray-400 w-4">#{i + 1}</span>
                    <span className="font-medium truncate">{c.name}</span>
                    <span className="font-mono text-[10px] text-gray-500">{c.family}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-[10px] text-gray-400 uppercase tracking-widest">{c.bucket}</span>
                    <span className="font-mono text-xs font-semibold">{c.score}</span>
                  </div>
                </li>
              ))}
            </ol>
          </details>
        )}
      </div>
    </section>
  );
}

export { relativeTime };
